<script lang="ts" setup>
import axios from 'axios'
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router/composables'
import { contactUsConfig as config } from './configs/vowpath'
import { texts } from '@/config/brands'
import { $getDeviceId, $getToken } from '@/helpers'
import i18n, { LOCALE_MAP } from '@/i18n'
import { useToastStore } from '@/stores'
import { isValidEmail } from '@/utils'

const { $toast } = useToastStore()
const route = useRoute()

const des = ref('')
const email = ref('')
const loading = ref(false)
const routePlatform = computed(() => route.query.platform as string | undefined)
const isEmbeddedMobile = computed(() => !!routePlatform.value)

function getIssueSubmitBaseURL() {
  if (import.meta.env.MODE === 'development')
    return 'https://story-api-test.rocnovel.com'

  return import.meta.env.VITE_API_BASE
}

async function submitIssue(body: PostIssueSubmitBody) {
  const token = $getToken()
  const equipmentId = $getDeviceId()
  const response = await axios.post<PostIssueSubmitResponse>('/issue/submit', body, {
    baseURL: getIssueSubmitBaseURL(),
    headers: {
      Platform: routePlatform.value || 3,
      versionCode: '168',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      clientGroupName: texts?.clientGroupName,
      contentType: '6',
      language: LOCALE_MAP[i18n.locale as keyof typeof LOCALE_MAP] || LOCALE_MAP.en,
      ...(token ? { Authorization: token } : {}),
      ...(equipmentId ? { equipmentId } : {}),
    },
  })

  return response.data
}

async function handleConfirm() {
  if (!isValidEmail(email.value)) {
    $toast(config.texts.emailErrorToast)
    return
  }

  loading.value = true

  try {
    const res = await submitIssue({
      issue: des.value,
      email: email.value,
    })

    if (res.code === 0 && res.data === true) {
      $toast(config.texts.successToast)
      des.value = ''
      email.value = ''
    }
    else {
      $toast(config.texts.failToast)
    }
  }
  catch (error: any) {
    $toast(error?.msg || config.texts.failToast)
  }
  finally {
    loading.value = false
  }
}

// 定义样式变量供 CSS v-bind 使用
const bgPrimary = config.colors.bgPrimary
const bgTertiary = config.colors.bgTertiary
const textPrimary = config.colors.textPrimary
const textTertiary = config.colors.textTertiary
const textPlaceholder = config.colors.textPlaceholder
const accentRed = config.colors.accentRed
const primaryColor = config.colors.primaryColor
const whiteColor = config.colors.whiteColor
</script>

<template>
  <div class="contact-us-page">
    <div v-if="!isEmbeddedMobile" class="h-44px">
      <BasicNavbar opacity class="color-n-text-2" />
    </div>
    <div class="form-container">
      <div class="form-item">
        <div class="form-title">
          <span class="required">*</span>{{ config.texts.feedbackTitle }}
        </div>
        <div class="textarea-wrapper">
          <textarea
            v-model="des"
            class="form-textarea"
            :placeholder="config.texts.feedbackPlaceholder"
            maxlength="100"
          />
          <div class="char-count">
            {{ des.length }}/100
          </div>
        </div>
      </div>

      <div class="form-item">
        <div class="form-title">
          <span class="required">*</span>{{ config.texts.emailTitle }}
        </div>
        <input
          v-model="email"
          type="text"
          class="form-input"
          :placeholder="config.texts.emailPlaceholder"
        >
      </div>

      <button
        :disabled="des === '' || email === '' || loading"
        class="submit-btn"
        :class="{ 'submit-btn-loading': loading }"
        @click="handleConfirm"
      >
        {{ loading ? config.texts.submittingButton : config.texts.submitButton }}
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.contact-us-page {
  min-height: 100vh;
  background: v-bind(bgPrimary);
}

.form-container {
  padding: 0 10px;
}

.form-item {
  margin-bottom: 20px;
}

.form-title {
  padding: 18px 0;
  display: flex;
  gap: 5px;
  align-items: center;
  font-size: 16px;
  color: v-bind(textPrimary);

  .required {
    color: v-bind(accentRed);
  }
}

.textarea-wrapper {
  position: relative;
}

.form-textarea {
  width: 100%;
  height: 130px;
  background: v-bind(bgTertiary);
  border-radius: 8px;
  border: none;
  padding: 12px;
  font-size: 14px;
  color: v-bind(textPrimary);
  resize: none;
  box-sizing: border-box;
  font-family: inherit;

  &::placeholder {
    color: v-bind(textPlaceholder);
  }

  &:focus {
    outline: none;
  }
}

.char-count {
  position: absolute;
  bottom: 8px;
  right: 12px;
  font-size: 12px;
  color: v-bind(textTertiary);
}

.form-input {
  width: 100%;
  height: 44px;
  background: v-bind(bgTertiary);
  border-radius: 8px;
  border: none;
  padding: 0 12px;
  font-size: 14px;
  color: v-bind(textPrimary);
  box-sizing: border-box;

  &::placeholder {
    color: v-bind(textPlaceholder);
  }

  &:focus {
    outline: none;
  }
}

.submit-btn {
  display: block;
  margin: 40px auto 0;
  width: 265px;
  height: 40px;
  border-radius: 60px;
  background: #BB7AFB;
  color: v-bind(whiteColor);
  font-size: 16px;
  border: none;
  cursor: pointer;
  transition: opacity 0.3s;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:not(:disabled):active {
    opacity: 0.8;
  }

  &-loading {
    opacity: 0.8;
  }
}
</style>
